import { useToast } from "@caspeco/casper-ui-library";
import { usePaymentTerminal } from "api/api-hooks/use-payment-terminal";
import StatusFailedIcon from "components/booking-status/status-failed-icon";
import ErrorTemplate from "components/error-components/error-template";
import { useError } from "hooks/use-error";
import { PageType } from "logger/page-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getSearchParams, UrlParamName } from "search-params";
import { filterSearchParams, PAYMENT_TERMINAL_PARAMS } from "search-params/filter-search-params";
import { PaymentErrors } from "types/validation-result";
import { Routes } from "routes";

const PaymentError = () => {
    const { t } = useTranslation();
    const { validationResult } = useError();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const { system, unitId } = useParams();
    const terminal = usePaymentTerminal();
    const { webBookingId, offerStartTime, isPaymentLink, isNoShow, isExperienceViewMode } =
        getSearchParams();

    const hasRun = useRef<boolean>(false);
    const isActivityRoute = location.pathname.includes(`/${Routes.ACTIVITY_BOOKING}`);

    const errorType = validationResult?.getGeneralError();

    usePageTracking({
        pageName: "PaymentError",
        pageType: PageType.Page,
        metadata: {
            errorType: errorType ? PaymentErrors[errorType] : "Unknown",
            errorCode: errorType,
            isNoShow: isNoShow || false,
            isPaymentLink: isPaymentLink || false,
            isWaitList: !!offerStartTime,
        },
    });

    const handleStartOver = () => {
        // Prefer URL params (always correct for the current page) and fall
        // back to localStorage only as a safety net.
        const targetSystem = system ?? localStorage.getItem("lastKnownSystemId");
        const targetUnitId = unitId ?? localStorage.getItem("lastKnownUnitId");
        if (!targetSystem || !targetUnitId) return;

        // Preserve non-payment query params (e.g. experienceView, guests,
        // children, date) so the user restarts in the same UI context.
        // Drop payment-specific params that would re-trigger the failed flow.
        const qs = filterSearchParams(window.location.search, [
            ...PAYMENT_TERMINAL_PARAMS,
            UrlParamName.IS_PAYMENT_LINK,
        ]);

        const activitySegment = isActivityRoute ? `/${Routes.ACTIVITY_BOOKING}` : "";
        const experienceSegment = isExperienceViewMode ? `/${Routes.EXPERIENCE_VIEW}` : "";
        const availabilitySegment = !isExperienceViewMode ? `/${Routes.AVAILABILITY}` : "";
        const path = `/${targetSystem}/${targetUnitId}${availabilitySegment}${activitySegment}${experienceSegment}${qs ? `?${qs}` : ""}`;

        window.location.replace(path);
    };

    const triggerToast = (title: string, message: string) => {
        showToast({
            type: "error",
            title: title,
            description: message,
            duration: null,
        });
    };

    const noShowOrPrepayment = isNoShow ? "noShow" : "payment";
    useEffect(() => {
        if (validationResult && !validationResult.isValid() && !hasRun.current) {
            hasRun.current = true;
            const isWaitList = offerStartTime;
            const errorType = validationResult.getGeneralError();

            let shouldShowToast = true;
            let toastMessage = "";
            switch (errorType) {
                case PaymentErrors.PaymentCanceled:
                case PaymentErrors.ProcessFailed:
                case PaymentErrors.RegistrationFailed:
                    toastMessage = t(`paymentErrors.${noShowOrPrepayment}.paymentCanceled`);
                    break;
                case PaymentErrors.DoubleBookingException:
                    toastMessage = t("paymentErrors.doubleBookingException");
                    if (isWaitList) {
                        shouldShowToast = false;
                        const urlDbe = `/booking/${system}/${webBookingId}`;
                        void navigate(urlDbe, {
                            state: { failedPaymentConfirmWaitList: true },
                        });
                    }
                    break;
                case PaymentErrors.AuthFailed:
                    toastMessage = t("paymentErrors.authFailed");
                    break;
                case PaymentErrors.CreditFailed:
                    toastMessage = t("paymentErrors.creditFailed");
                    break;
                case PaymentErrors.Expired:
                    toastMessage = t("paymentErrors.expired");
                    break;
                default:
                    break;
            }

            if (isPaymentLink || isWaitList) {
                const url = `/booking/${system}/${webBookingId}${isPaymentLink ? `?${UrlParamName.IS_PAYMENT_LINK}=true` : ""}`;
                void navigate(url);
            }
            const toastTitle = t(`paymentErrors.toastTitle.${noShowOrPrepayment}`);
            if (shouldShowToast) triggerToast(toastTitle, toastMessage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ErrorTemplate
            icon={<StatusFailedIcon />}
            title={t(`paymentErrors.dialogue.${noShowOrPrepayment}.title`)}
            message={t(`paymentErrors.dialogue.${noShowOrPrepayment}.message`)}
            btnCallback={handleStartOver}
            btnText={t("errorInformation.startOver")}
            isBtnLoading={terminal.isPending}
            trackingId="error-payment-start-over"
        />
    );
};

export default PaymentError;
